import React from 'react';
import Edge from 'components/canvas/components/edge';
import { usePrepareNodeEdges } from 'components/canvas/hooks/use-prepare-surface-nodes';
import { SurfaceNodePointsTo, SurfaceNodeUnion } from 'graphql/types';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import { usePrevious } from 'hooks';
import { notEqual } from 'utils';

export interface EdgesProps {
  nodes: SurfaceNodeUnion[];
}

const defaultProps = {};

function Edges(props: EdgesProps) {
  const currentSurface = useCurrentSurface();

  const nodeEdges = usePrepareNodeEdges(props.nodes, currentSurface?.id);

  return (
    <>
      {nodeEdges.map(function (link) {
        return <EdgeWrapper key={link.from.id + link.to.id} link={link} />;
      })}
    </>
  );
}

function EdgeWrapper({ link }) {
  const isOneOfLinkNodesUpdated = useIsOneOfLinkNodesUpdated(link);
  const previousLink = usePrevious(link);

  const linkSettingsChanged = Boolean(
    previousLink && notEqual(previousLink?.settings, link.settings),
  );

  return React.useMemo(() => {
    return (
      <Edge link={link} isOneOfLinkNodesUpdated={isOneOfLinkNodesUpdated} />
    );
  }, [isOneOfLinkNodesUpdated, link.settings?.updatedAt]);
}

Edges.defaultProps = defaultProps;

function useIsOneOfLinkNodesUpdated(
  link: SurfaceNodePointsTo,
  callback?: () => void,
): boolean {
  const { findNodeById } = useFindNodeInCache();

  const dummyLink = {
    from: findNodeById(link.from.id),
    to: findNodeById(link.to.id),
  };

  const previousLink = usePrevious(dummyLink);

  const fromNodeUpdated =
    previousLink?.from?.updatedAt !== dummyLink?.from?.updatedAt;

  const toNodeUpdated =
    previousLink?.to?.updatedAt !== dummyLink?.to?.updatedAt;

  React.useEffect(() => {
    callback && callback();
  }, [fromNodeUpdated, toNodeUpdated]);

  return fromNodeUpdated || toNodeUpdated;
}

export default Edges;
